/*
 * Optimized Product Picker JS
 *
 * Handles:
 *   - Quantity pill toggle (writes to product form's quantity input)
 *   - Auto-snap secondary options when primary changes to an incompatible combo
 *     (e.g. when user clicks Chicken but pack size was "3 x 30 treats", snap
 *      pack size to an available option)
 */

(function () {
  'use strict';

  /* -------- Quantity pills -------------------------------------------- */

  function initQuantityPills(scope) {
    const root = scope || document;
    const groups = root.querySelectorAll('.optimized-quantity-pills');
    groups.forEach((group) => {
      if (group.dataset.optimizedInit === '1') return;
      group.dataset.optimizedInit = '1';

      const pills = group.querySelectorAll('.optimized-quantity-pill');
      const wrap = group.closest('.optimized-quantity-selector');
      const badge = wrap?.querySelector('[data-bulk-badge]');
      const qtyInput = wrap?.querySelector('[data-optimized-qty-input]');
      const thumb = group.querySelector('[data-optimized-qty-thumb]');
      const badgeTrigger = parseInt(group.dataset.badgeTrigger, 10) || 2;
      const maxPillQty = parseInt(group.dataset.maxQty, 10) || 3;

      const positionThumb = (animated) => {
        if (!thumb) return;
        const selected = group.querySelector('.optimized-quantity-pill--selected');
        if (!selected) {
          thumb.style.opacity = '0';
          return;
        }
        const groupRect = group.getBoundingClientRect();
        const pillRect = selected.getBoundingClientRect();
        const left = pillRect.left - groupRect.left;
        const width = pillRect.width;
        if (!animated) {
          // Disable transition for the initial or layout-change position set
          const prev = thumb.style.transition;
          thumb.style.transition = 'none';
          thumb.style.left = `${left}px`;
          thumb.style.width = `${width}px`;
          thumb.style.opacity = '1';
          // Force reflow then restore transition
          void thumb.offsetWidth;
          thumb.style.transition = prev || '';
        } else {
          thumb.style.left = `${left}px`;
          thumb.style.width = `${width}px`;
          thumb.style.opacity = '1';
        }
      };

      const morePill = wrap?.querySelector('[data-optimized-qty-more]');
      const summary = wrap?.parentElement?.querySelector('[data-optimized-custom-qty-summary]');
      const summaryValue = summary?.querySelector('[data-optimized-custom-qty-value]');
      const summaryEdit = summary?.querySelector('[data-optimized-custom-qty-open]');

      // Modal lives as a sibling to .optimized-quantity-selector
      const sectionRoot = wrap?.parentElement || document;
      const modal = sectionRoot.querySelector('[data-optimized-custom-qty-modal]');
      const modalInput = modal?.querySelector('[data-optimized-custom-qty]');
      const modalInc = modal?.querySelector('[data-optimized-custom-qty-inc]');
      const modalDec = modal?.querySelector('[data-optimized-custom-qty-dec]');
      const modalConfirm = modal?.querySelector('[data-optimized-custom-qty-confirm]');
      const modalCancels = modal?.querySelectorAll('[data-optimized-custom-qty-cancel]') || [];

      // "Other" pill is selected only when qty exceeds the max normal pill
      // (so qty=3 still highlights the "3" pill, and only 4+ falls to "Other")
      const isMoreQty = (q) => q > maxPillQty;

      const otherLabelEl = wrap?.querySelector('[data-optimized-qty-other-label]');

      const setSummaryVisible = (qty) => {
        if (!summary) return;
        const visible = isMoreQty(qty);
        if (visible) summary.removeAttribute('hidden');
        else summary.setAttribute('hidden', '');
        if (summaryValue && visible) summaryValue.textContent = String(qty);
        // Update the "Other" pill label to show the current qty when active
        if (otherLabelEl) {
          otherLabelEl.textContent = visible ? String(qty) : 'Other';
        }
      };

      const applyQty = (selectedQty) => {
        const qty = Math.max(1, parseInt(selectedQty, 10) || 1);
        pills.forEach((pill) => {
          const q = parseInt(pill.dataset.qty, 10);
          const isMore = pill === morePill;
          const isSelected = isMore ? isMoreQty(qty) : q === qty;
          pill.classList.toggle('optimized-quantity-pill--selected', isSelected);
          pill.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });
        if (qtyInput) {
          qtyInput.value = String(qty);
          qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (badge) {
          badge.classList.toggle('optimized-quantity-badge--active', qty >= badgeTrigger);
        }
        setSummaryVisible(qty);
        // If the "Other" label changed (-> reflects new number or back to "Other"),
        // its width changes. Position immediately then again on next frame
        // in case the layout shifts after paint.
        positionThumb(true);
        requestAnimationFrame(() => positionThumb(true));
        refreshSkioDependentUI();
      };

      // --- Pill clicks ---
      pills.forEach((pill) => {
        pill.addEventListener('click', () => {
          if (pill === morePill) {
            openModal();
            return;
          }
          const q = parseInt(pill.dataset.qty, 10);
          if (!isNaN(q)) applyQty(q);
        });
      });

      // --- Modal ---
      const otherMinQty = maxPillQty + 1;

      function openModal() {
        if (!modal) return;
        // Preload input with the current qty if it's already in "other" range,
        // otherwise default to otherMinQty (qty > all normal pills).
        const current = parseInt(qtyInput?.value, 10) || otherMinQty;
        if (modalInput) {
          modalInput.value = String(Math.max(current, otherMinQty));
        }
        if (typeof modal.showModal === 'function') {
          modal.showModal();
        } else {
          modal.setAttribute('open', '');
        }
        setTimeout(() => {
          modalInput?.focus();
          modalInput?.select?.();
        }, 0);
      }

      function closeModal() {
        if (!modal) return;
        if (typeof modal.close === 'function' && modal.open) modal.close();
        else modal.removeAttribute('open');
      }

      function confirmModal() {
        if (!modalInput) return;
        let val = parseInt(modalInput.value, 10);
        if (isNaN(val) || val < otherMinQty) val = otherMinQty;
        applyQty(val);
        closeModal();
      }

      modalConfirm?.addEventListener('click', confirmModal);
      modalCancels.forEach((el) => el.addEventListener('click', closeModal));
      summaryEdit?.addEventListener('click', openModal);

      modalInput?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          confirmModal();
        }
      });
      modalInc?.addEventListener('click', () => {
        if (!modalInput) return;
        const val = parseInt(modalInput.value, 10) || otherMinQty;
        modalInput.value = String(val + 1);
      });
      modalDec?.addEventListener('click', () => {
        if (!modalInput) return;
        const val = parseInt(modalInput.value, 10) || otherMinQty;
        modalInput.value = String(Math.max(val - 1, otherMinQty));
      });

      // Click outside content to close
      modal?.addEventListener('click', (e) => {
        if (e.target === modal) closeModal();
      });

      // --- Thumb initial position + resize handling ---
      // Try synchronous + async for belt-and-suspenders timing
      positionThumb(false);
      requestAnimationFrame(() => positionThumb(false));
      setTimeout(() => positionThumb(false), 100);
      if (typeof ResizeObserver === 'function') {
        const ro = new ResizeObserver(() => positionThumb(false));
        ro.observe(group);
      }
      window.addEventListener('resize', () => positionThumb(false));
    });
  }

  /* -------- Variant auto-snap ----------------------------------------- */

  /*
   * When the user clicks a radio in the flavor (first) option, the secondary
   * option (e.g. pack size) may still point at a value that doesn't combine
   * with the new flavor into a real variant. Shopify's section re-fetch then
   * returns variant=null, which breaks Skio (variantId = NaN).
   *
   * We intercept the change at the capture phase (runs BEFORE VariantSelects'
   * bubble-phase listener) and, if the current combination is invalid,
   * force-check the secondary radio that matches an available variant for
   * the newly selected primary value.
   */

  function getProductVariants(variantSelects) {
    const el = variantSelects?.querySelector('[data-optimized-variants]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  function getCheckedOptionValues(variantSelects) {
    const fieldsets = variantSelects.querySelectorAll('fieldset');
    const values = [];
    fieldsets.forEach((fs) => {
      const checked = fs.querySelector('input[type="radio"]:checked');
      values.push(checked?.value ?? null);
    });
    return values;
  }

  function findVariantByOptions(variants, option1, option2, option3) {
    return variants.find(
      (v) =>
        (option1 == null || v.option1 === option1) &&
        (option2 == null || v.option2 === option2) &&
        (option3 == null || v.option3 === option3)
    );
  }

  function findFirstAvailableVariantForPrimary(variants, option1) {
    return (
      variants.find((v) => v.option1 === option1 && v.available) ||
      variants.find((v) => v.option1 === option1)
    );
  }

  function selectRadioByValue(variantSelects, fieldsetIndex, value) {
    const fieldsets = variantSelects.querySelectorAll('fieldset');
    const fs = fieldsets[fieldsetIndex];
    if (!fs) return false;
    const inputs = fs.querySelectorAll('input[type="radio"]');
    for (const inp of inputs) {
      if (inp.value === value) {
        inp.checked = true;
        return true;
      }
    }
    return false;
  }

  function autoSnapOnChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    const variantSelects = input.closest('variant-selects');
    if (!variantSelects) return;

    const variants = getProductVariants(variantSelects);
    if (!variants) return;

    const fieldsets = Array.from(variantSelects.querySelectorAll('fieldset'));
    const changedFieldsetIndex = fieldsets.findIndex((fs) => fs.contains(input));
    if (changedFieldsetIndex !== 0) return; // only snap when primary (flavor) changes

    const checkedValues = getCheckedOptionValues(variantSelects);
    const [v1, v2, v3] = checkedValues;

    const combo = findVariantByOptions(variants, v1, v2, v3);
    if (combo) return; // current combination is valid; nothing to do

    // Pick first available variant for the new primary value, then update
    // the remaining fieldsets' radios to match.
    const fallback = findFirstAvailableVariantForPrimary(variants, v1);
    if (!fallback) return;

    if (v2 !== fallback.option2 && fallback.option2 != null) {
      selectRadioByValue(variantSelects, 1, fallback.option2);
    }
    if (v3 !== fallback.option3 && fallback.option3 != null) {
      selectRadioByValue(variantSelects, 2, fallback.option3);
    }
  }

  // Capture phase so we run BEFORE VariantSelects' bubble-phase listener
  document.addEventListener(
    'change',
    (event) => {
      if (event.target && event.target.closest && event.target.closest('variant-selects')) {
        autoSnapOnChange(event);
      }
    },
    true
  );

  /* -------- Nest benefits inside Subscribe card ----------------------- */

  /*
   * The optimized_offer_benefits Liquid block renders AFTER Skio in the
   * block order (Liquid can't inject into Skio's DOM). We relocate it
   * into the first .skio-group-container (Subscribe, since onetime_first
   * is false) so the benefits visually live inside the subscribe card.
   * Re-run on Skio DOM mutations so it survives re-renders.
   */

  function nestBenefits() {
    const benefits = document.querySelector('.optimized-picker-wrapper .optimized-offer-benefits');
    if (!benefits) return;
    const subscribeCard = document.querySelector(
      '.optimized-picker-wrapper .skio-group-container'
    );
    if (!subscribeCard) return;
    if (subscribeCard.contains(benefits)) return;
    subscribeCard.appendChild(benefits);
  }

  /*
   * Capture Skio's per-unit price for each card and cache it on
   * element.dataset.optimizedBasePrice so qty multiplication survives
   * across Skio re-renders without accumulating.
   *
   * Skio emits plain per-unit price text (e.g. "$26.99") on every render.
   * We detect that by checking if the current textContent has only one
   * currency-number token. If it does, that's Skio's fresh per-unit — we
   * recapture and re-display. Our display may write a different format
   * (multiplied by qty) which won't match and so won't be recaptured.
   */
  /*
   * Source of truth for per-unit prices: the selected variant JSON Dawn
   * injects into <variant-selects> as [data-selected-variant]. Updated on
   * every variant change by product-info.js. More reliable than scraping
   * Skio's DOM (Skio doesn't refresh the non-selected card's price).
   */
  function getSelectedVariant() {
    const el = document.querySelector('variant-selects [data-selected-variant]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  function getSelectedSubscriptionPrice(variant) {
    if (!variant) return null;
    // Prefer the first subscription selling plan allocation's price
    const alloc = (variant.selling_plan_allocations || []).find((a) => a.price != null);
    if (alloc != null) return alloc.price / 100;
    return variant.price != null ? variant.price / 100 : null;
  }

  function getSelectedOneTimePrice(variant) {
    return variant && variant.price != null ? variant.price / 100 : null;
  }

  function priceElBase(priceEl) {
    if (!priceEl) return null;
    const text = priceEl.textContent.trim();
    const tokens = (text.match(/[0-9.,]+/g) || []).filter((s) => /[0-9]/.test(s));
    const stored = parseFloat(priceEl.dataset.optimizedBasePrice);
    const lastDisplayQty = parseFloat(priceEl.dataset.optimizedDisplayQty);
    const hasStored = !isNaN(stored);
    const hasLastQty = !isNaN(lastDisplayQty);

    if (tokens.length === 1) {
      const parsed = parsePrice(text);
      if (parsed == null) return hasStored ? stored : null;

      // If the current single-token text matches the total we last wrote,
      // this is our own write — trust stored. Compare against the exact
      // written total (not stored × qty) because that total may include a
      // volume discount; otherwise we'd recapture our discounted total as a
      // fresh base and compound the discount on every re-render.
      const lastTotal = parseFloat(priceEl.dataset.optimizedDisplayTotal);
      if (hasStored && !isNaN(lastTotal) && parsed.toFixed(2) === lastTotal.toFixed(2)) {
        return stored;
      }
      if (hasStored && hasLastQty && parsed.toFixed(2) === (stored * lastDisplayQty).toFixed(2)) {
        return stored;
      }
      // Otherwise it's Skio's fresh per-unit update — recapture.
      priceEl.dataset.optimizedBasePrice = String(parsed);
      delete priceEl.dataset.optimizedDisplayQty;
      delete priceEl.dataset.optimizedDisplayTotal;
      return parsed;
    }

    // Multi-token text (e.g. our crossout + total). Can't safely recapture.
    return hasStored ? stored : null;
  }

  function currencyOf(text) {
    return (text.match(/[^0-9.,\s]+/) || ['$'])[0];
  }

  /*
   * Multiply Skio's per-unit subscribe-card price by the current quantity
   * so the customer sees a real total when qty > 1. Also render a crossed-
   * out one-time equivalent next to it when the two prices differ, so the
   * subscription savings are legible as "was $X · now $Y".
   *
   * Per-unit comes from Skio's own displayed price (via priceElBase()),
   * which already reflects the selling-plan / first-order discount — we
   * never re-apply those. The one thing Skio's widget does NOT show is its
   * volume discount (it's applied at cart level), so we mirror it here via
   * applyBulkDiscount(), using the same trigger/% configured on the
   * optimized_qty_selector block that the One-Time card uses. That block's
   * bulk % must match Skio's volume-discount config.
   */
  function addCrossoutPrice() {
    const wrapper = document.querySelector('.optimized-picker-wrapper');
    if (!wrapper) return;
    // Clear any legacy "You save $X per delivery" line from prior renders —
    // Skio owns the savings narrative now.
    wrapper.querySelector('.optimized-sub-savings')?.remove();

    const cards = wrapper.querySelectorAll('.skio-group-container');
    if (cards.length < 2) return;
    const subCard = cards[0];
    const subPriceEl = subCard.querySelector('.skio-price');
    if (!subPriceEl) return;

    const variant = getSelectedVariant();
    // subPerUnit tracks Skio's own displayed per-unit price (whatever
    // discount stack Skio applies). Falls back to variant JSON only if
    // Skio hasn't rendered yet.
    const subPerUnit = priceElBase(subPriceEl) ?? getSelectedSubscriptionPrice(variant);
    const oneTimePerUnit = getSelectedOneTimePrice(variant);
    if (subPerUnit == null) return;

    const qty = getQuantity();
    const currency = currencyOf(subPriceEl.textContent) || '$';
    // Mirror Skio's cart-level volume discount on the display.
    const subEach = applyBulkDiscount(subPerUnit, qty);
    const subTotal = subEach * qty;
    const subText = formatPrice(subTotal, currency);

    // Crossout = the One-Time card's total at this qty (same bulk rule), so
    // the two cards compare like-for-like.
    const oneTimeEach = applyBulkDiscount(oneTimePerUnit, qty);
    const wantsCrossout = oneTimeEach != null && oneTimeEach > subEach + 0.001;
    const oneTimeText = wantsCrossout ? formatPrice(oneTimeEach * qty, currency) : '';

    // Short-circuit if DOM already matches desired state (prevents observer loops).
    const currentOrig = subPriceEl.querySelector('.optimized-orig-price');
    const currentOrigText = currentOrig?.textContent?.trim() || '';
    const currentSuffix = subPriceEl.querySelector('.optimized-each-price');
    const currentSuffixText = currentSuffix?.textContent?.trim() || '';
    const expectedSuffixText = qty > 1 ? `${formatPrice(subEach, currency)} each` : '';
    const currentTextNodes = Array.from(subPriceEl.childNodes)
      .filter((n) => n.nodeType === Node.TEXT_NODE)
      .map((n) => n.textContent.trim())
      .filter(Boolean)
      .join('');
    if (
      currentTextNodes === subText &&
      (wantsCrossout ? currentOrigText === oneTimeText : !currentOrig) &&
      currentSuffixText === expectedSuffixText
    ) {
      return;
    }

    // Wipe every child (Skio wraps its per-unit price in a <span>, so just
    // clearing text nodes leaves Skio's span in place — that's what caused
    // the "$29.99 $22.49 $22.49" duplicate). Rebuild the element from
    // scratch: crossout, total, "$X each" suffix.
    subPriceEl.textContent = '';

    if (wantsCrossout) {
      const orig = document.createElement('span');
      orig.className = 'optimized-orig-price';
      orig.textContent = oneTimeText;
      subPriceEl.appendChild(orig);
    }
    subPriceEl.appendChild(document.createTextNode(subText));
    setEachSuffix(subPriceEl, subEach, qty, currency);
    subPriceEl.dataset.optimizedBasePrice = String(subPerUnit);
    subPriceEl.dataset.optimizedDisplayQty = String(qty);
    subPriceEl.dataset.optimizedDisplayTotal = subTotal.toFixed(2);
  }

  /*
   * Set (or clear) a "$X each" suffix on a Skio price element.
   * The suffix is a trailing span.optimized-each-price so text-based
   * price writes don't clobber it.
   */
  function setEachSuffix(priceEl, perUnit, qty, currency) {
    if (!priceEl) return;
    let suffix = priceEl.querySelector('.optimized-each-price');
    const shouldShow = qty > 1 && perUnit != null;
    if (shouldShow) {
      const text = `${formatPrice(perUnit, currency)} each`;
      if (!suffix) {
        suffix = document.createElement('span');
        suffix.className = 'optimized-each-price';
        priceEl.appendChild(suffix);
      }
      if (suffix.textContent !== text) suffix.textContent = text;
    } else if (suffix) {
      suffix.remove();
    }
  }

  /*
   * Render the One-Time card's price from the selected variant's `price`
   * (× qty). Skio does not refresh this card on variant change, so we
   * take ownership of the display.
   */
  function updateOneTimePrice() {
    const wrapper = document.querySelector('.optimized-picker-wrapper');
    if (!wrapper) return;
    const cards = wrapper.querySelectorAll('.skio-group-container');
    if (cards.length < 2) return;
    const oneTimeCard = cards[1];
    const priceEl = oneTimeCard.querySelector('.skio-price');
    if (!priceEl) return;

    const variant = getSelectedVariant();
    const baseRaw = getSelectedOneTimePrice(variant);
    if (baseRaw == null) return;
    const qty = getQuantity();
    const base = applyBulkDiscount(baseRaw, qty);
    const currency = currencyOf(priceEl.textContent) || '$';
    const totalText = formatPrice(base * qty, currency);
    const origTotalText = formatPrice(baseRaw * qty, currency);
    const wantsCrossout = Math.abs(baseRaw - base) > 0.001;

    // Idempotent write: wipe, then rebuild: [crossout?] total [each?]
    const currentOrig = priceEl.querySelector('.optimized-orig-price');
    const currentOrigText = currentOrig?.textContent?.trim() || '';
    const currentSuffix = priceEl.querySelector('.optimized-each-price');
    const currentSuffixText = currentSuffix?.textContent?.trim() || '';
    const expectedSuffixText = qty > 1 ? `${formatPrice(base, currency)} each` : '';
    const currentTotal = Array.from(priceEl.childNodes)
      .filter((n) => n.nodeType === Node.TEXT_NODE)
      .map((n) => n.textContent.trim())
      .filter(Boolean)
      .join('');
    if (
      currentTotal === totalText &&
      (wantsCrossout ? currentOrigText === origTotalText : !currentOrig) &&
      currentSuffixText === expectedSuffixText
    ) {
      return;
    }

    // Clear all children — our crossout, any Skio-injected price spans, and
    // stray text nodes — so the element is a clean slate we fully own.
    priceEl.textContent = '';

    if (wantsCrossout) {
      const orig = document.createElement('span');
      orig.className = 'optimized-orig-price';
      orig.textContent = origTotalText;
      priceEl.appendChild(orig);
    }
    priceEl.appendChild(document.createTextNode(totalText));
    setEachSuffix(priceEl, base, qty, currency);
  }

  /*
   * First-order promo pricing.
   * When shop.metafields.custom.first_sub_promo_active is true, the server
   * emits `data-promo-active="true"` on the picker wrapper and a
   * `[data-first-order-price]` strip inside the subscribe card.
   * We compute (subscribe price) * (1 - promo_percent/100), format as
   * currency, and fill the strip.
   */
  function parsePrice(text) {
    if (!text) return null;
    const clean = text.replace(/[^0-9.,]/g, '').replace(/,/g, '');
    const num = parseFloat(clean);
    return isNaN(num) ? null : num;
  }

  function formatPrice(amount, currencySymbol) {
    if (amount == null || isNaN(amount)) return '';
    return `${currencySymbol}${amount.toFixed(2)}`;
  }

  function getQuantity() {
    const qtyInput = document.querySelector(
      '.optimized-picker-wrapper [data-optimized-qty-input]'
    );
    const qty = parseInt(qtyInput?.value, 10);
    return !qty || isNaN(qty) || qty < 1 ? 1 : qty;
  }

  /*
   * Bulk-quantity discount (display-side mirror of a Shopify
   * automatic discount that applies at checkout). Configured on the
   * optimized_qty_selector block: triggers at `badge_trigger_quantity`,
   * reduces per-unit price by `bulk_discount_percent`.
   */
  function getBulkDiscountInfo() {
    const pills = document.querySelector('.optimized-picker-wrapper .optimized-quantity-pills');
    if (!pills) return { trigger: Infinity, pct: 0 };
    const trigger = parseInt(pills.dataset.badgeTrigger, 10);
    const pct = parseFloat(pills.dataset.bulkDiscountPercent);
    return {
      trigger: !trigger || isNaN(trigger) ? Infinity : trigger,
      pct: !pct || isNaN(pct) ? 0 : pct,
    };
  }

  function applyBulkDiscount(perUnit, qty) {
    if (perUnit == null) return perUnit;
    const { trigger, pct } = getBulkDiscountInfo();
    if (!(qty >= trigger && pct > 0)) return perUnit;
    // Match checkout rounding: both Shopify's one-time "Bulk discount" and
    // Skio's "Volume Discount" compute the discount per unit and round it
    // DOWN to the cent ($29.99 × 5% = $1.4995 → $1.49), then multiply by
    // qty. Plain float math rounds the other way and drifts 1–3¢ per line.
    const unitCents = Math.round(perUnit * 100);
    const discountCents = Math.floor((unitCents * pct) / 100 + 1e-9);
    return (unitCents - discountCents) / 100;
  }

  /*
   * Previously computed the first-order promo strip ("Your first order
   * $X, then $Y per delivery") from variant.selling_plan_allocations
   * × bulk × promo%. Skio now applies the same promo + bulk internally
   * for subscriptions, so this on-page calculation was double-counting
   * the discount. The promo-strip DOM has been removed from the Liquid
   * snippet — this function stays as a no-op in case older cached HTML
   * still ships the strip.
   */
  function updatePromoPricing() {
    return;
  }

  /*
   * The Skio selling plan's own discount percent (the number Skio shows
   * in its ".skio-save" label — "Save X% ongoing") is currently 25% in
   * the Skio config, but the on-page copy we want is 10% ongoing. Rather
   * than editing the selling plan (which would change the actual
   * discount charged), we override the LABEL only. Skio's discount math
   * at checkout is untouched — this only swaps the badge text on the
   * subscribe card. When the selling plan's own discount matches the
   * label we want, remove this override.
   */
  var SUB_SAVINGS_LABEL = 'Save 10% ongoing';
  function overrideSkioSavingsLabel() {
    const wrapper = document.querySelector('.optimized-picker-wrapper');
    if (!wrapper) return;
    const labels = wrapper.querySelectorAll('.skio-save');
    labels.forEach((el) => {
      if (el.textContent.trim() !== SUB_SAVINGS_LABEL) {
        el.textContent = SUB_SAVINGS_LABEL;
      }
    });
  }

  function refreshSkioDependentUI() {
    nestBenefits();
    updateOneTimePrice();
    addCrossoutPrice();
    updatePromoPricing();
    overrideSkioSavingsLabel();
  }

  function watchSkioForNest() {
    const skioHost = document.querySelector('.optimized-picker-wrapper .shopify-app-block');
    if (!skioHost) return;
    const observer = new MutationObserver(() => refreshSkioDependentUI());
    observer.observe(skioHost, { childList: true, subtree: true, characterData: true });
  }

  /*
   * On first load, show the second media item as the active/featured one
   * instead of the product's first image. Only applies when the shopper
   * hasn't landed on a specific variant (?variant=...) — if a variant URL
   * is present, Dawn's variant-featured-media logic already picked the
   * right image. After this runs once, normal variant-change behavior
   * takes over.
   */
  function showSecondImageByDefault() {
    // Respect explicit variant links
    if (new URLSearchParams(window.location.search).has('variant')) return;

    const gallery = document.querySelector('media-gallery');
    if (!gallery || typeof gallery.setActiveMedia !== 'function') return;
    // `.product__media-item` is the slide class Dawn renders each media in.
    // Filter further to those with section-prefixed ids (real slides, not
    // deferred media sub-elements that also have data-media-id).
    const slides = Array.from(
      gallery.querySelectorAll('.product__media-item[data-media-id]')
    );
    if (slides.length < 2) return;
    const secondMediaId = slides[1].dataset.mediaId;
    if (!secondMediaId) return;
    if (slides[1].classList.contains('is-active')) return;
    try {
      gallery.setActiveMedia(secondMediaId, false);
    } catch (_) {
      /* swallow — don't break page if gallery API changes */
    }
  }

  /*
   * "Our Ingredients" header button: opens a modal dialog containing the
   * product's ingredients content.
   */
  function initIngredientsButton() {
    const btn = document.querySelector('[data-optimized-ingredients-btn]');
    if (!btn || btn.dataset.optimizedInit === '1') return;
    btn.dataset.optimizedInit = '1';

    const modal = document.querySelector('[data-optimized-ingredients-modal]');
    if (!modal) return;

    const openModal = () => {
      if (typeof modal.showModal === 'function') modal.showModal();
      else modal.setAttribute('open', '');
    };
    const closeModal = () => {
      if (typeof modal.close === 'function' && modal.open) modal.close();
      else modal.removeAttribute('open');
    };

    btn.addEventListener('click', openModal);
    modal.querySelectorAll('[data-optimized-ingredients-close]').forEach((el) =>
      el.addEventListener('click', closeModal)
    );
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });
  }

  /*
   * Make the whole Skio card clickable — not just the radio circle.
   * Delegate at the wrapper level so we don't have to re-bind on Skio
   * re-renders. Ignore clicks on interactive descendants (dropdowns,
   * buttons, links) so their native behavior isn't hijacked.
   */
  function initFullCardClick() {
    const wrapper = document.querySelector('.optimized-picker-wrapper');
    if (!wrapper || wrapper.dataset.optimizedFullCardClickInit === '1') return;
    wrapper.dataset.optimizedFullCardClickInit = '1';

    wrapper.addEventListener('click', (event) => {
      const card = event.target.closest('.skio-group-container');
      if (!card) return;
      // Don't interfere with real interactive elements
      if (event.target.closest('select, input, button, a, [role="button"], label')) return;
      const radio = card.querySelector('input.skio-group-input');
      if (!radio || radio.checked) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      radio.dispatchEvent(new Event('click', { bubbles: true }));
    });
  }

  /* -------- Init ------------------------------------------------------ */

  /*
   * Dawn's MediaGallery.setActiveMedia auto-scrolls the page up to the
   * featured media whenever the variant changes. On the optimized picker
   * that's jarring — the user is usually scrolled near the picker/offer
   * cards and doesn't want to jump back to the image. Patch the prototype
   * so the window.scrollTo call becomes a no-op while still allowing the
   * media-list scroll-left (which keeps the right slide active).
   */
  /*
   * Prevent `.focus()`-induced page jumps. When the user clicks a flavor
   * swatch label, the browser transfers focus to the hidden radio input
   * which by default scrolls it into view — yanking the page up to the
   * picker. Permanently force preventScroll on every focus() call for
   * this template's lifetime.
   */
  function patchGlobalFocusNoScroll() {
    const origFocus = HTMLElement.prototype.focus;
    if (origFocus.__optimizedPatched) return;
    HTMLElement.prototype.focus = function (o) {
      return origFocus.call(this, { ...(o || {}), preventScroll: true });
    };
    HTMLElement.prototype.focus.__optimizedPatched = true;
  }

  function patchProductInfoFocusScroll() {
    customElements.whenDefined('product-info').then(() => {
      const ctor = customElements.get('product-info');
      const Proto = ctor && ctor.prototype;
      if (!Proto || Proto.__optimizedFocusPatched) return;
      const orig = Proto.renderProductInfo;
      if (typeof orig !== 'function') return;
      Proto.renderProductInfo = function (opts) {
        const origFocus = HTMLElement.prototype.focus;
        HTMLElement.prototype.focus = function (o) {
          return origFocus.call(this, { ...(o || {}), preventScroll: true });
        };
        // Save scroll and restore it for the next few frames — Dawn's
        // fetch-and-replace shortens the DOM briefly, which lets the
        // browser's scroll anchoring (or just clamping) yank the page
        // up. We pin the scroll back until the new section stabilizes.
        const savedY = window.scrollY;
        let ticks = 0;
        const pin = () => {
          if (Math.abs(window.scrollY - savedY) > 2) {
            window.scrollTo(0, savedY);
          }
          if (ticks++ < 60) requestAnimationFrame(pin);
        };
        requestAnimationFrame(pin);
        try {
          return orig.call(this, opts);
        } finally {
          setTimeout(() => {
            HTMLElement.prototype.focus = origFocus;
          }, 1500);
        }
      };
      Proto.__optimizedFocusPatched = true;
    });
  }

  function patchMediaGalleryAutoScroll() {
    customElements.whenDefined('media-gallery').then(() => {
      const ctor = customElements.get('media-gallery');
      const Proto = ctor && ctor.prototype;
      if (!Proto || Proto.__optimizedScrollPatched) return;
      const orig = Proto.setActiveMedia;
      if (typeof orig !== 'function') return;
      Proto.setActiveMedia = function (...args) {
        const realScrollTo = window.scrollTo.bind(window);
        // Neutralize only vertical page scrolls triggered by
        // setActiveMedia's trailing setTimeout. Horizontal scrollTo on
        // slider children uses element.scrollTo, not window.scrollTo.
        window.scrollTo = function (optOrX, y) {
          if (optOrX && typeof optOrX === 'object' && 'top' in optOrX) return;
          return realScrollTo(optOrX, y);
        };
        try {
          return orig.apply(this, args);
        } finally {
          // Restore on the next macrotask so the pending setTimeout
          // inside setActiveMedia also sees the muted version.
          setTimeout(() => {
            window.scrollTo = realScrollTo;
          }, 100);
        }
      };
      Proto.__optimizedScrollPatched = true;
    });
  }

  // Focus-scroll patch must run before any user interaction. Do it at
  // script-parse time, not DOMContentLoaded.
  patchGlobalFocusNoScroll();

  document.addEventListener('DOMContentLoaded', () => {
    patchMediaGalleryAutoScroll();
    patchProductInfoFocusScroll();
    initQuantityPills();
    initFullCardClick();
    initIngredientsButton();
    // Give the media gallery a frame to mount its custom element
    setTimeout(showSecondImageByDefault, 100);
    // Skio may render asynchronously; try a few times
    refreshSkioDependentUI();
    setTimeout(refreshSkioDependentUI, 300);
    setTimeout(refreshSkioDependentUI, 1000);
    watchSkioForNest();

    // Subscribe to Dawn's variantChange pubsub so we refresh after
    // product-info.js finishes swapping DOM (Skio re-renders on variant id
    // change — prices, save badge, etc. all reset).
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        // Variant change: clear cached per-unit bases so the next refresh
        // re-captures from whatever price Skio renders for the new variant
        document
          .querySelectorAll('.optimized-picker-wrapper .skio-price')
          .forEach((el) => {
            delete el.dataset.optimizedBasePrice;
            delete el.dataset.optimizedDisplayQty;
          });
        // Multiple timeouts so we catch Skio's async re-render
        setTimeout(refreshSkioDependentUI, 200);
        setTimeout(refreshSkioDependentUI, 700);
        setTimeout(refreshSkioDependentUI, 1500);
      });
    }
  });

  /*
   * Fail-safe scroll restoration for variant changes. Something (browser
   * scroll anchoring with the swapped section, or an autofocus on a
   * re-rendered radio) moves the viewport to the top of the product
   * image after a variant click. Save scroll on swatch-click-intent and
   * pin it for ~1.5s across the async re-render.
   */
  function pinScrollDuringVariantChange() {
    let savedY = null;
    let stopAt = 0;
    let intervalId = null;
    const releasePin = () => {
      savedY = null;
      stopAt = 0;
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
    const rafPin = () => {
      if (savedY == null) return;
      if (Math.abs(window.scrollY - savedY) > 2) window.scrollTo(0, savedY);
      if (performance.now() < stopAt) requestAnimationFrame(rafPin);
    };
    const startPin = () => {
      savedY = window.scrollY;
      // Short window — just long enough to catch the one scroll yank
      // that happens from the section swap, not long enough to fight
      // the user if they want to scroll themselves.
      stopAt = performance.now() + 700;
      requestAnimationFrame(rafPin);
      if (intervalId) clearInterval(intervalId);
      intervalId = setInterval(() => {
        if (performance.now() >= stopAt) {
          releasePin();
          return;
        }
        if (Math.abs(window.scrollY - savedY) > 2) window.scrollTo(0, savedY);
      }, 32);
    };

    document.addEventListener('mousedown', (e) => {
      if (!e.target?.closest?.('.optimized-variant-picker')) return;
      startPin();
    }, true);
    document.addEventListener('touchstart', (e) => {
      if (!e.target?.closest?.('.optimized-variant-picker')) return;
      startPin();
    }, true);
    document.addEventListener('change', (e) => {
      if (!e.target?.closest?.('.optimized-variant-picker')) return;
      startPin();
    }, true);

    // User-initiated scroll intent: release the pin immediately so we
    // don't fight the user. wheel = mouse/trackpad, touchmove = finger.
    const userScrollRelease = () => {
      if (savedY != null) releasePin();
    };
    window.addEventListener('wheel', userScrollRelease, { passive: true });
    window.addEventListener('touchmove', userScrollRelease, { passive: true });
    window.addEventListener('keydown', (e) => {
      // Arrow keys / PageUp/Down / Space / Home / End scroll the page
      if (
        e.key === 'ArrowDown' || e.key === 'ArrowUp' ||
        e.key === 'PageDown' || e.key === 'PageUp' ||
        e.key === 'Home' || e.key === 'End' || e.key === ' '
      ) userScrollRelease();
    });
  }

  /* -------- First-sub gift checkbox ----------------------------------- */

  function initGiftCheckbox(scope) {
    const root = scope || document;
    const lockups = root.querySelectorAll('[data-optimized-gift]');
    lockups.forEach((lockup) => {
      if (lockup.dataset.optimizedGiftInit === '1') return;
      lockup.dataset.optimizedGiftInit = '1';

      const checkbox = lockup.querySelector('[data-optimized-gift-checkbox]');
      const statusEl = lockup.querySelector('[data-optimized-gift-status]');
      const variantId = parseInt(lockup.dataset.variantId, 10);
      const discountCode = lockup.dataset.discountCode;

      if (!checkbox || !variantId) return;

      syncGiftStateFromCart(lockup, checkbox, statusEl, variantId);

      checkbox.addEventListener('change', async () => {
        checkbox.disabled = true;
        const wantChecked = checkbox.checked;
        try {
          if (wantChecked) {
            const item = await addGiftToCart(variantId);
            if (item && item.key) {
              lockup.dataset.giftLineKey = item.key;
            }
            if (discountCode) applyDiscountCode(discountCode);
            setGiftStatus(statusEl, 'Added to cart');
          } else {
            let key = lockup.dataset.giftLineKey;
            if (!key) key = await findGiftLineKey(variantId);
            if (key) {
              await removeCartLine(key);
              delete lockup.dataset.giftLineKey;
            }
            setGiftStatus(statusEl, '');
          }
          publishCartUpdate();
        } catch (err) {
          // Roll back the visual state if the network call failed
          checkbox.checked = !wantChecked;
          console.error('[optimized-gift]', err);
          setGiftStatus(statusEl, 'Could not update cart — please try again');
        } finally {
          checkbox.disabled = false;
        }
      });
    });
  }

  async function syncGiftStateFromCart(lockup, checkbox, statusEl, variantId) {
    try {
      const key = await findGiftLineKey(variantId);
      if (key) {
        lockup.dataset.giftLineKey = key;
        checkbox.checked = true;
        setGiftStatus(statusEl, 'Added to cart');
      }
    } catch (_) {
      // Non-fatal — leave checkbox unchecked
    }
  }

  async function findGiftLineKey(variantId) {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const cart = await res.json();
    const match = (cart.items || []).find(
      (i) => i.variant_id === variantId && i.properties && i.properties._gift_lockup
    );
    return match ? match.key : null;
  }

  async function addGiftToCart(variantId) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: { _gift_lockup: '1' },
          },
        ],
      }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error('add failed: ' + res.status + ' ' + text);
    }
    const data = await res.json();
    return data.items ? data.items[0] : data;
  }

  async function removeCartLine(key) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: key, quantity: 0 }),
    });
    if (!res.ok) throw new Error('remove failed: ' + res.status);
    return res.json();
  }

  function applyDiscountCode(code) {
    if (!code) return;
    // GET /discount/<code> sets the discount in the customer's session.
    // The Buy X Get Y rule (configured in Shopify admin) then makes the
    // gift line item free at checkout — Shopify enforces any eligibility
    // gating (first-time customer, etc.) server-side at that point.
    fetch('/discount/' + encodeURIComponent(code), {
      method: 'GET',
      credentials: 'same-origin',
    }).catch(() => {});
  }

  function setGiftStatus(el, text) {
    if (!el) return;
    if (text) {
      el.textContent = text;
      el.hidden = false;
    } else {
      el.textContent = '';
      el.hidden = true;
    }
  }

  function publishCartUpdate() {
    // Dawn doesn't have a universal cart-refresh event; emit both the
    // generic one and dispatch a "cart:refresh" so cart-drawer-like
    // components can rebind. Anything that doesn't listen is a no-op.
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof publish === 'function') {
      try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'optimized-gift' }); } catch (_) {}
    }
  }

  // Re-init after product-info.js swaps the section HTML on variant change
  pinScrollDuringVariantChange();
  initGiftCheckbox();
  document.addEventListener('variant:change', () => {
    initQuantityPills();
    initGiftCheckbox();
  });
  document.addEventListener(
    'change',
    (e) => {
      if (e.target && e.target.closest && e.target.closest('variant-selects')) {
        setTimeout(() => initQuantityPills(), 300);
      }
    },
    true
  );
})();
